import { useCallback, useEffect, useRef, useState } from 'react';

// Lightweight dual-axis pointer drag hook for the dashboard grid resize handles.
// The shared useResizableHandle in lib/hooks/common is x-only; rather than fork it
// we keep this scoped to the dashboard. Reports a *delta* (in px) on every move and
// commits via onCommit at drag end.
//
// Optional `autoScrollContainer` (y-axis only): when the pointer enters a band near
// the container's top or bottom edge during a drag, the container scrolls in that
// direction. The reported delta accounts for scrollTop drift so the resize stays
// glued to the cursor's actual position.

type Axis = 'x' | 'y';

type Options<TStart> = {
  axis: Axis;
  // Snapshot the starting state when the drag begins. Returned value is passed back
  // to onMove/onCommit so the consumer doesn't have to chase stale closure values.
  getStart: () => TStart;
  onMove: (start: TStart, deltaPx: number) => void;
  onCommit: (start: TStart, deltaPx: number) => void;
  enabled?: boolean;
  // Optional. When set and axis === 'y', the hook auto-scrolls this element while
  // dragging near its top or bottom edge.
  autoScrollContainer?: HTMLElement | null;
};

const AUTO_SCROLL_BAND_PX = 80; // distance from an edge that triggers auto-scroll
const AUTO_SCROLL_MAX_SPEED = 4; // px per animation frame at the very edge

export const useGridResizeHandle = <TStart>({
  axis,
  getStart,
  onMove,
  onCommit,
  enabled = true,
  autoScrollContainer,
}: Options<TStart>) => {
  const [isDragging, setIsDragging] = useState(false);
  const stateRef = useRef<{
    start: number;
    pointerId: number;
    snapshot: TStart | null;
    startScrollTop: number;
    lastClientY: number;
    lastReportedDelta: number;
    lastReportedAtMs: number;
  }>({
    start: 0,
    pointerId: -1,
    snapshot: null,
    startScrollTop: 0,
    lastClientY: 0,
    lastReportedDelta: 0,
    lastReportedAtMs: 0,
  });
  const isDraggingRef = useRef(false);
  const getStartRef = useRef(getStart);
  const onMoveRef = useRef(onMove);
  const onCommitRef = useRef(onCommit);
  const containerRef = useRef<HTMLElement | null>(autoScrollContainer ?? null);
  getStartRef.current = getStart;
  onMoveRef.current = onMove;
  onCommitRef.current = onCommit;
  containerRef.current = autoScrollContainer ?? null;

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!enabled) return;
      e.preventDefault();
      isDraggingRef.current = true;
      setIsDragging(true);
      stateRef.current = {
        start: axis === 'x' ? e.clientX : e.clientY,
        pointerId: e.pointerId,
        snapshot: getStartRef.current(),
        startScrollTop: containerRef.current?.scrollTop ?? 0,
        lastClientY: e.clientY,
        lastReportedDelta: 0,
        lastReportedAtMs: performance.now(),
      };
      if (e.currentTarget.setPointerCapture) {
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    },
    [axis, enabled],
  );

  useEffect(() => {
    if (!isDragging) return;

    // Raw delta — accounts for scrollTop drift since pointerDown so dragging past the
    // visible bottom keeps tracking the cursor's *document* position, not its viewport.
    const rawDelta = (clientCoord: number): number => {
      if (axis === 'x') return clientCoord - stateRef.current.start;
      const scrollDelta =
        (containerRef.current?.scrollTop ?? stateRef.current.startScrollTop) -
        stateRef.current.startScrollTop;
      return clientCoord - stateRef.current.start + scrollDelta;
    };

    // When the cursor is past either edge of the container, cap how fast the delta is
    // allowed to *change* per millisecond. Without this, flicking the cursor far past
    // the viewport makes the row jump by hundreds of px in a single frame. The clamp
    // is symmetric: limits growth in the direction of the outward drag, in either axis
    // sign.
    const reportableDelta = (clientCoord: number, clientY: number): number => {
      const target = rawDelta(clientCoord);
      const container = axis === 'y' ? containerRef.current : null;
      const rect = container?.getBoundingClientRect();
      const cursorPastEdge = rect !== undefined && (clientY > rect.bottom || clientY < rect.top);
      const now = performance.now();
      if (!cursorPastEdge) {
        stateRef.current.lastReportedDelta = target;
        stateRef.current.lastReportedAtMs = now;
        return target;
      }
      // px/ms cap derived from auto-scroll's px/frame. Symmetric: the change |Δdelta|
      // is bounded, so dragging hard up or hard down both feel controlled.
      const maxPxPerMs = AUTO_SCROLL_MAX_SPEED / (1000 / 60);
      const elapsedMs = Math.max(1, now - stateRef.current.lastReportedAtMs);
      const maxChange = maxPxPerMs * elapsedMs;
      const previous = stateRef.current.lastReportedDelta;
      const desiredChange = target - previous;
      const allowedChange =
        desiredChange > 0
          ? Math.min(desiredChange, maxChange)
          : Math.max(desiredChange, -maxChange);
      const allowed = previous + allowedChange;
      stateRef.current.lastReportedDelta = allowed;
      stateRef.current.lastReportedAtMs = now;
      return allowed;
    };

    const handlePointerMove = (e: PointerEvent) => {
      if (!isDraggingRef.current) return;
      if (e.pointerId !== stateRef.current.pointerId) return;
      const snapshot = stateRef.current.snapshot;
      if (snapshot === null) return;
      stateRef.current.lastClientY = e.clientY;
      const delta = reportableDelta(axis === 'x' ? e.clientX : e.clientY, e.clientY);
      onMoveRef.current(snapshot, delta);
    };

    const handlePointerUpOrCancel = (e: PointerEvent) => {
      if (!isDraggingRef.current) return;
      if (e.pointerId !== stateRef.current.pointerId) return;
      const snapshot = stateRef.current.snapshot;
      if (snapshot !== null) {
        // Commit with the last clamped delta so a fast release doesn't snap the row to
        // the unbounded raw target.
        const delta = stateRef.current.lastReportedDelta;
        onCommitRef.current(snapshot, delta);
      }
      isDraggingRef.current = false;
      setIsDragging(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUpOrCancel);
    window.addEventListener('pointercancel', handlePointerUpOrCancel);

    // Auto-scroll ticker. Bidirectional: scrolls down when cursor is near/past the
    // bottom, up when near/past the top. Runs on every animation frame regardless of
    // whether the scroll actually moved — that's important because once the user
    // parks the cursor at the screen edge, no more pointermove events fire and the
    // ticker is the only thing keeping the resize delta growing.
    let rafId: number | null = null;
    if (axis === 'y' && containerRef.current) {
      const tick = () => {
        if (!isDraggingRef.current) return;
        const container = containerRef.current;
        if (container) {
          const rect = container.getBoundingClientRect();
          const cursorY = stateRef.current.lastClientY;

          // Distance into the band. Positive when in-band; 0 outside the band; can
          // saturate at 1 once the cursor is at or past the edge.
          const distancePastBottom = cursorY - (rect.bottom - AUTO_SCROLL_BAND_PX);
          const distancePastTop = rect.top + AUTO_SCROLL_BAND_PX - cursorY;

          let speed = 0;
          if (distancePastBottom > 0) {
            const intensity = Math.min(1, distancePastBottom / AUTO_SCROLL_BAND_PX);
            speed = AUTO_SCROLL_MAX_SPEED * intensity * intensity;
          } else if (distancePastTop > 0) {
            const intensity = Math.min(1, distancePastTop / AUTO_SCROLL_BAND_PX);
            speed = -AUTO_SCROLL_MAX_SPEED * intensity * intensity;
          }

          if (speed !== 0) {
            container.scrollTop = container.scrollTop + speed;
            // Surface a fresh delta every tick — even if scrollTop hit a clamp (start
            // or end), the user's cursor staying past the edge means we still want
            // the resize to inch outward via the past-edge clamp.
            const snapshot = stateRef.current.snapshot;
            if (snapshot !== null) {
              const delta = reportableDelta(cursorY, cursorY);
              onMoveRef.current(snapshot, delta);
            }
          }
        }
        rafId = window.requestAnimationFrame(tick);
      };
      rafId = window.requestAnimationFrame(tick);
    }

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUpOrCancel);
      window.removeEventListener('pointercancel', handlePointerUpOrCancel);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, [axis, isDragging]);

  return { isDragging, onPointerDown: handlePointerDown };
};
